<template>
  <div class="ap-page">

    <!-- HERO -->
    <section class="ap-hero">
      <div class="ap-hero-inner">
        <div class="ap-hero-text">
          <p class="ap-eyebrow">Programs</p>
          <h1 class="ap-h1">NexaScale Apprenticeship.</h1>
          <p class="ap-hero-body">
            The gap between learning and working is where most people get stuck. 
            The Apprenticeship program exists to close that gap — connecting community 
            members to real job opportunities, simulated work experience, and industry 
            placements with our partners. Stay in touch to hear when the next 
            opportunity opens.
          </p>
          <a class="ap-btn" href="https://forms.gle/nivXcK3MwJ8ZeFRp7" target="_blank">
            Stay in touch →
          </a>
        </div>
        <div class="ap-hero-image">
          <img src="../../assets/images/programs/apprenticeship-min.jpg" alt="NexaScale Apprenticeship" />
        </div>
      </div>
    </section>

    <!-- CONTENT -->
    <section class="ap-content">
      <div class="ap-content-inner">

        <div class="ap-section">
          <h2 class="ap-section-title">Our objective</h2>
          <div class="ap-section-body">
            <p>
              You can learn all you want — but until you've worked on something real, 
              with real deadlines and real teammates, the learning doesn't fully land. 
              That's the problem the Apprenticeship program is designed to solve.
            </p>
            <p>
              We connect community members to job opportunities, internships, and 
              structured work experience with companies and organisations that are 
              actively looking for emerging talent. The goal is simple: get you from 
              learning to doing, and from doing to hired.
            </p>
          </div>
        </div>

        <div class="ap-section">
          <h2 class="ap-section-title">Our partners</h2>
          <div class="ap-section-body">
            <p>
              We've built relationships with organisations that believe in early-career 
              talent and are willing to invest in it. Opportunities vary by partner — 
              some offer internships, some offer structured bootcamps, some offer paid 
              open-source contributions.
            </p>
            <div class="ap-partners">
              <div class="ap-partner">
                <span class="ap-partner-name">Miva University</span>
                <span class="ap-partner-detail">Academic credit pathways and structured work placements</span>
              </div>
              <div class="ap-partner">
                <span class="ap-partner-name">Co.Lab Canada</span>
                <span class="ap-partner-detail">Cross-functional remote product teams — real product work, real experience</span>
              </div>
              <!---<div class="ap-partner">
                <span class="ap-partner-name">Alvative</span>
                <span class="ap-partner-detail">Technical training and placement opportunities</span>
              </div>-->
              <div class="ap-partner">
                <span class="ap-partner-name">Genesys Tech Hub, Enugu</span>
                <span class="ap-partner-detail">In-person hub access, training, and local industry connections</span>
              </div>
            </div>
          </div>
        </div>

        <div class="ap-section">
          <h2 class="ap-section-title">What you get</h2>
          <div class="ap-section-body">
            <p>
              Opportunities vary depending on the partner and the cohort, but typically include 
              one or more of the following:
            </p>
            <div class="ap-benefits">
              <div class="ap-benefit">
                <span class="ap-benefit-dot"></span>
                <div>
                  <span class="ap-benefit-name">Internship or entry-level job placements</span>
                  <span class="ap-benefit-note">Direct connections to hiring partners actively recruiting from our community</span>
                </div>
              </div>
              <div class="ap-benefit">
                <span class="ap-benefit-dot"></span>
                <div>
                  <span class="ap-benefit-name">Simulated work experience</span>
                  <span class="ap-benefit-note">Structured bootcamps that replicate real industry environments and workflows</span>
                </div>
              </div>
              <div class="ap-benefit">
                <span class="ap-benefit-dot"></span>
                <div>
                  <span class="ap-benefit-name">Paid open-source contributions</span>
                  <span class="ap-benefit-note">Opportunities to contribute to real projects and get compensated for the work</span>
                </div>
              </div>
              <div class="ap-benefit">
                <span class="ap-benefit-dot"></span>
                <div>
                  <span class="ap-benefit-name">Training &amp; upskilling</span>
                  <span class="ap-benefit-note">Partner-led technical training tailored to the role you're being prepared for</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="ap-section ap-section--cta">
          <h2 class="ap-section-title ap-section-title--light">Who can apply?</h2>
          <div class="ap-section-body ap-section-body--light">
            <p>
              Every community member is eligible to apply — there's no blanket requirement 
              beyond being part of NexaScale. Each opportunity comes with its own criteria 
              set by the partner, and those requirements are shared in full when applications 
              open. You'll interview with the partner directly, and they make the final call.
            </p>
            <p>
              The best thing you can do in the meantime is stay active in the community, 
              keep building, and make sure you're in the loop when announcements drop.
            </p>
            <br />
            <a class="ap-btn ap-btn--yellow" href="https://forms.gle/nivXcK3MwJ8ZeFRp7" target="_blank">
              Stay in touch →
            </a>
          </div>
        </div>

      </div>
    </section>

  </div>
</template>

<style scoped>
.ap-page {
  min-height: var(--pageheight, 100vh);
  margin-top: 80px;
  text-align: left;
}
.ap-page * { box-sizing: border-box; }
.ap-page p, .ap-page h1, .ap-page h2 { text-align: left; }

/* HERO */
.ap-hero {
  background: #191616;
  padding: 80px 7%;
}
.ap-hero-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 72px;
  align-items: center;
}
.ap-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #FFB812;
  display: block;
  margin-bottom: 16px;
}
.ap-h1 {
  font-size: clamp(2.2rem, 5vw, 4rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: #ffffff;
  margin: 0 0 20px;
  word-break: break-word;
}
.ap-hero-body {
  font-size: 0.97rem;
  line-height: 1.8;
  color: rgba(255,255,255,0.55);
  margin: 0 0 32px;
  max-width: 520px;
}
.ap-btn {
  display: inline-block;
  background: #FFB812;
  color: #191616;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 12px 26px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s;
}
.ap-btn:hover { background: #FF9914; }
.ap-btn--yellow { background: #FFB812; color: #191616; }
.ap-btn--yellow:hover { background: #FF9914; }

.ap-hero-image { border-radius: 16px; overflow: hidden; }
.ap-hero-image img {
  width: 100%;
  display: block;
  border-radius: 16px;
  object-fit: cover;
  object-position: center top;
  max-height: 480px;
}

/* CONTENT */
.ap-content { background: #ffffff; }
.ap-content-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 7%;
}

.ap-section {
  padding: 72px 0;
  border-bottom: 1px solid #e8e4e0;
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 60px;
  align-items: start;
}
.ap-section:last-child { border-bottom: none; }
.ap-section--cta {
  background: #191616;
  margin: 0 -7%;
  padding: 72px 7%;
  border-bottom: none;
}

.ap-section-title {
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #191616;
  margin: 0;
  position: sticky;
  top: 100px;
  word-break: break-word;
}
.ap-section-title--light { color: #ffffff; }

.ap-section-body p {
  font-size: 0.97rem;
  line-height: 1.8;
  color: #555250;
  margin: 0 0 16px;
}
.ap-section-body p:last-of-type { margin-bottom: 0; }
.ap-section-body--light p { color: rgba(255,255,255,0.6); }

/* PARTNERS */
.ap-partners {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e8e4e0;
  margin-top: 24px;
}
.ap-partner {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 24px;
  padding: 18px 0;
  border-bottom: 1px solid #e8e4e0;
}
.ap-partner:last-child { border-bottom: none; }
.ap-partner-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #191616;
  white-space: nowrap;
  flex-shrink: 0;
}
.ap-partner-detail {
  font-size: 0.82rem;
  color: #aaa8a4;
  text-align: right;
}

/* BENEFITS */
.ap-benefits {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e8e4e0;
  margin-top: 20px;
}
.ap-benefit {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid #e8e4e0;
}
.ap-benefit:last-child { border-bottom: none; }
.ap-benefit-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #FFB812;
  flex-shrink: 0;
  margin-top: 6px;
}
.ap-benefit-name {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: #191616;
  margin-bottom: 4px;
}
.ap-benefit-note {
  display: block;
  font-size: 0.8rem;
  color: #aaa8a4;
  line-height: 1.5;
}

/* RESPONSIVE */
@media (max-width: 900px) {
  .ap-hero-inner { grid-template-columns: 1fr; gap: 40px; }
  .ap-hero-image { order: -1; }
  .ap-hero-image img { max-height: 280px; }
  .ap-section { grid-template-columns: 1fr; gap: 20px; padding: 52px 0; }
  .ap-section--cta { padding: 52px 5%; margin: 0 -5%; }
  .ap-section-title { position: static; }
  .ap-hero { padding: 60px 5%; }
  .ap-content-inner { padding: 0 5%; }
  .ap-partner { flex-direction: column; gap: 4px; }
  .ap-partner-detail { text-align: left; }
}
</style>